import {useState, useEffect} from 'react'
import Sticky from 'react-stickynode'
import type {JSX} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {Block} from '@/services/kontent-ai/types'
import {useWindowSize} from '@/utilities/hooks/use-window-size'
import {customBrands} from '../helpers'

export type StickyBarContentItem = IContentItem<{
	brand: Elements.MultipleChoiceElement
}>

const background = {
	primary: 'bg-theme-main',
	secondary: 'bg-blue-900',
	galliprant: 'bg-galliprant',
	trifexis: 'bg-trifexis',
	micotil: 'bg-micotil',
	interceptorplus: 'bg-interceptorplus',
	capstar: 'bg-capstar',
	credelio: 'bg-credelio',
	bewormready: 'bg-bewormready',
	entyce: 'bg-entyce',
	elura: 'bg-elura',
	comfortis: 'bg-comfortis',
	advocate: 'bg-advocate',
	experior_green: 'bg-experior_green',
	hemicell: 'bg-hemicell',
}

const Bar = ({
	brand = 'primary',
	expanded = false,
	fixed = false,
	id = '',
	className = '',
}: {
	brand?: keyof typeof background
	expanded?: boolean
	fixed?: boolean
	id?: string
	className?: string
}): JSX.Element => {
	let height = ''
	if (expanded) {
		height = 'h-12'
	} else {
		height = fixed ? 'h-1 md:h-2' : 'h-8'
	}

	return (
		<hr
			className={`${className} border-0 transition-all duration-200 ease-out ${height} ${
				background[brand]
			} ${
				fixed
					? 'after:content-[" "] -z-1 md:absolute md:bottom-0 md:inline-block md:h-1 md:w-full md:shadow-secondary'
					: ''
			}`}
			id={id}
		/>
	)
}

export const StickyBarBlock: Block<StickyBarContentItem> = ({block}) => {
	const [topOffset, setTopOffset] = useState(0)

	const size = useWindowSize()

	useEffect(() => {
		const secondaryHeader = document.querySelector(
			'[data-secondary-header]'
		)
		const hasSecondaryHeader =
			secondaryHeader &&
			secondaryHeader instanceof HTMLElement &&
			!size.isMobile
		const newTopOffset = hasSecondaryHeader
			? 48 + secondaryHeader.scrollHeight
			: 48
		if (newTopOffset !== topOffset) {
			setTopOffset(newTopOffset)
		}
	}, [size.isMobile, topOffset])

	const selectedBrandOrFallback =
		block.elements.brand[0]?.codename ?? 'primary'
	const className = customBrands[selectedBrandOrFallback]?.background ?? ''

	return (
		<div data-kontent-element-codename={block.system.codename}>
			<Sticky bottomBoundary="#bottom-bar" innerZ="1" top={topOffset}>
				{({status}) => (
					<Bar
						brand={
							selectedBrandOrFallback as keyof typeof background
						}
						className={className}
						fixed={status === Sticky.STATUS_FIXED}
					/>
				)}
			</Sticky>
		</div>
	)
}
